import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Card, Chip, Alert, CircularProgress, Button, Grid, Typography,
  Tooltip, IconButton, LinearProgress
} from '@mui/material';
import {
  Refresh, Storage, CheckCircle, Warning as WarningIcon, Error as ErrorIcon,
  Speed
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { CardTitle, BodyText, SecondaryText, PrimaryButton, GlassmorphicCard } from '../styles/sharedStyles';

const PoolCard = styled(GlassmorphicCard)({
  height: '100%',
  '&:hover': {
    transform: 'translateY(-5px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
  },
});

const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'status'
})(({ status }) => {
  let bgColor = 'rgba(158, 158, 158, 0.1)';
  let textColor = '#9e9e9e';
  let borderColor = 'rgba(158, 158, 158, 0.3)';

  if (status === 'ONLINE' || status === 'online') {
    bgColor = 'rgba(0, 255, 136, 0.1)';
    textColor = '#00ff88';
    borderColor = 'rgba(0, 255, 136, 0.3)';
  } else if (status === 'DEGRADED' || status === 'degraded') {
    bgColor = 'rgba(255, 193, 7, 0.1)';
    textColor = '#ffc107';
    borderColor = 'rgba(255, 193, 7, 0.3)';
  } else if (status === 'OFFLINE' || status === 'offline' || status === 'FAULTED' || status === 'faulted') {
    bgColor = 'rgba(255, 68, 68, 0.1)';
    textColor = '#ff4444';
    borderColor = 'rgba(255, 68, 68, 0.3)';
  }

  return {
    background: bgColor,
    border: `1px solid ${borderColor}`,
    color: textColor,
    fontWeight: 600,
    fontSize: '0.75rem',
    height: '24px',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'scale(1.05)',
    },
  };
});

const StoragePools = ({ apiBaseUrl, token }) => {
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPools = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${apiBaseUrl}/api/pools/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPools(response.data.pools || []);
    } catch (err) {
      console.error('Error fetching pools:', err);
      setError(err.response?.data?.detail || 'Failed to fetch pool information');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, token]);

  useEffect(() => {
    fetchPools();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchPools, 30000);
    return () => clearInterval(interval);
  }, [fetchPools]);

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getUsagePercentage = (used, total) => {
    if (!total || total === 0) return 0;
    return Math.round((used / total) * 100);
  };

  if (loading && pools.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: '#00d4ff' }} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: '16px',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 68, 68, 0.3)',
            backgroundColor: 'rgba(255, 68, 68, 0.05)',
            '& .MuiAlert-message': {
              color: '#ff8888'
            }
          }}
        >
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <CardTitle sx={{ m: 0 }}>Storage Pools</CardTitle>
        <Tooltip title="Refresh pool data">
          <IconButton
            onClick={fetchPools}
            disabled={loading}
            sx={{
              color: '#00d4ff',
              background: 'rgba(0, 212, 255, 0.1)',
              border: '1px solid rgba(0, 212, 255, 0.3)',
              borderRadius: '12px',
              '&:hover': {
                background: 'rgba(0, 212, 255, 0.2)',
                borderColor: 'rgba(0, 212, 255, 0.6)',
              },
              '&:disabled': {
                opacity: 0.6,
              }
            }}
          >
            <Refresh sx={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {pools.length > 0 ? (
        <Grid container spacing={3}>
          {pools.map((pool, index) => {
            const usedSpace = pool.allocated || 0;
            const totalSpace = pool.size || 0;
            const usagePercent = getUsagePercentage(usedSpace, totalSpace);

            return (
              <Grid item xs={12} sm={6} md={4} key={pool.name || index}>
                <PoolCard>
                  <Box sx={{ p: 3 }}>
                    {/* Header with name and status */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                        <Storage sx={{ fontSize: 32, color: '#00d4ff' }} />
                        <Box>
                          <CardTitle sx={{ m: 0, fontSize: '1.1rem' }}>
                            {pool.name || `Pool ${index + 1}`}
                          </CardTitle>
                          <SecondaryText sx={{ m: 0, fontSize: '0.875rem' }}>
                            {pool.type || 'Unknown Type'}
                          </SecondaryText>
                        </Box>
                      </Box>
                    </Box>

                    {/* Status */}
                    <Box sx={{ mb: 3, pb: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <StatusChip
                        status={pool.status || 'UNKNOWN'}
                        label={pool.status || 'UNKNOWN'}
                      />
                    </Box>

                    {/* Usage Bar */}
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <BodyText sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)', m: 0 }}>
                          Storage Usage
                        </BodyText>
                        <BodyText sx={{ fontSize: '0.875rem', fontWeight: 600, m: 0 }}>
                          {usagePercent}%
                        </BodyText>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={usagePercent}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            background: usagePercent > 90
                              ? 'linear-gradient(90deg, #ff4444, #ff6b6b)'
                              : usagePercent > 75
                              ? 'linear-gradient(90deg, #ffc107, #ffaa00)'
                              : 'linear-gradient(90deg, #00d4ff, #0099ff)',
                            borderRadius: 4,
                          },
                        }}
                      />
                    </Box>

                    {/* Space Details */}
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ mb: 1.5 }}>
                        <BodyText sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)', mb: 0.5 }}>
                          Used Space
                        </BodyText>
                        <BodyText sx={{ fontSize: '1rem', fontWeight: 600, m: 0 }}>
                          {formatBytes(usedSpace)}
                        </BodyText>
                      </Box>
                      <Box sx={{ mb: 1.5 }}>
                        <BodyText sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)', mb: 0.5 }}>
                          Total Capacity
                        </BodyText>
                        <BodyText sx={{ fontSize: '1rem', fontWeight: 600, m: 0 }}>
                          {formatBytes(totalSpace)}
                        </BodyText>
                      </Box>
                      <Box>
                        <BodyText sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)', mb: 0.5 }}>
                          Available Space
                        </BodyText>
                        <BodyText sx={{ fontSize: '1rem', fontWeight: 600, m: 0, color: usagePercent > 90 ? '#ff4444' : '#00ff88' }}>
                          {formatBytes(totalSpace - usedSpace)}
                        </BodyText>
                      </Box>
                    </Box>

                    {/* Extra info if available */}
                    {pool.errors !== undefined && (
                      <Box sx={{ pb: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)', pt: 2 }}>
                        <BodyText sx={{ fontSize: '0.875rem', color: pool.errors > 0 ? '#ff4444' : '#00ff88', m: 0 }}>
                          Errors: {pool.errors}
                        </BodyText>
                      </Box>
                    )}
                  </Box>
                </PoolCard>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Box sx={{
          textAlign: 'center',
          py: 6,
          color: 'rgba(255, 255, 255, 0.7)',
          background: 'rgba(255, 255, 255, 0.02)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.06)',
        }}>
          <Storage sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
          <Typography variant="body1">
            No pools found
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default StoragePools;
